/**
 * MultiAgentLayoutVariant — pure LAYOUT rearrangements of the multi-agent IDE.
 *
 * These change ONLY how the existing panels (sidebar · editor · chat ·
 * run-details) are arranged, sized and shown. No chat / editor / agent
 * functionality changes — every variant reuses the exact same components.
 *
 * Each variant is intentionally distinct AT REST (empty screen, no editor open),
 * not just once content streams in, so they're easy to tell apart and pick:
 *
 *   classic  — full sidebar · editor · chat · full run-details   (4 regions)
 *   focus    — no chrome; one centered narrow reading column      (1 region)
 *   command  — thin left rail · chat · WIDE run-details console   (right-heavy)
 *   split    — chat on the left, code editor always on the right  (2 columns)
 *   zen      — full-bleed chat edge-to-edge, no side chrome        (1 region)
 *
 * Exposed as /multi-agent/<variant> routes so they can be run side by side.
 */
export type MultiAgentLayoutVariant =
  | "classic"
  | "focus"
  | "command"
  | "split"
  | "zen";

/** How a side panel is presented. `hidden` = off-canvas, revealed by a small
 *  floating chevron; `rail` = 40px icon strip; `full` = the resizable column. */
export type PanelMode = "full" | "rail" | "hidden";

export interface LayoutVariantConfig {
  /** Short label shown in the header pill. */
  label: string;
  /** One-line description (tooltip + picker copy). */
  description: string;
  /** Left metadata/sessions sidebar presentation. */
  sidebar: PanelMode;
  /** Right run-details panel presentation. */
  right: PanelMode;
  /** Default width of the right run-details panel (px) when `full`. */
  rightWidth: number;
  /** Chat reading width: full column, or a centered measure. */
  chat: "full" | "narrow" | "wide";
  /** `ondemand` = editor appears when a file is opened (classic behaviour);
   *  `split` = the editor pane is ALWAYS shown beside the chat (placeholder
   *  until a file is opened) so the two-column split reads as the layout. */
  editor: "ondemand" | "split";
}

export const DEFAULT_LAYOUT_VARIANT: MultiAgentLayoutVariant = "classic";

export const LAYOUT_VARIANTS: Record<MultiAgentLayoutVariant, LayoutVariantConfig> = {
  classic: {
    label: "Classic",
    description: "Full sidebar · editor · chat · run details (current layout)",
    sidebar: "full",
    right: "full",
    rightWidth: 288,
    chat: "full",
    editor: "ondemand",
  },
  focus: {
    label: "Focus",
    description: "No chrome — one centered narrow reading column",
    sidebar: "hidden",
    right: "hidden",
    rightWidth: 288,
    chat: "narrow",
    editor: "ondemand",
  },
  command: {
    label: "Command",
    description: "Thin left rail, chat, and a WIDE always-open run-details console",
    sidebar: "rail",
    right: "full",
    rightWidth: 420,
    chat: "full",
    editor: "ondemand",
  },
  split: {
    label: "Split",
    description: "Chat on the left, code editor always on the right",
    sidebar: "rail",
    right: "hidden",
    rightWidth: 288,
    chat: "full",
    editor: "split",
  },
  zen: {
    label: "Zen",
    description: "Full-bleed chat edge-to-edge, no side chrome",
    sidebar: "hidden",
    right: "hidden",
    rightWidth: 288,
    chat: "full",
    editor: "ondemand",
  },
};

export function layoutConfig(variant: MultiAgentLayoutVariant | undefined): LayoutVariantConfig {
  return LAYOUT_VARIANTS[variant ?? DEFAULT_LAYOUT_VARIANT] ?? LAYOUT_VARIANTS.classic;
}
